import { Controller } from "@hotwired/stimulus";

// Page behaviour for /settings: scrollspy + anchor scrolling for the section
// nav, unsaved-change tracking on the single big form, and the small controls
// around secret fields (reveal, clear chip). Mounted on the page's scroll
// container so the nav, the form and the save bar are all in scope — the page
// scrolls inside that element, not the window, which is also why nav clicks
// scroll it here instead of relying on the browser's anchor jump.
export default class SettingsPageController extends Controller {
  static targets = ["link", "savebar", "statusText"];

  connect() {
    this.dirty = false;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.setActive(entry.target.id);
        }
      },
      // A band across the top quarter of the viewport: whichever section
      // occupies it is the one being read.
      { rootMargin: "0px 0px -75% 0px" }
    );
    this.element.querySelectorAll(".settings-section").forEach((s) => this.observer.observe(s));

    this.onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        this.element.querySelector("form")?.requestSubmit();
      }
    };
    this.onBeforeVisit = (event) => {
      if (this.dirty && !window.confirm("Leave settings with unsaved changes?")) {
        event.preventDefault();
      }
    };
    this.onBeforeUnload = (event) => {
      if (!this.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    document.addEventListener("keydown", this.onKey);
    document.addEventListener("turbo:before-visit", this.onBeforeVisit);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  disconnect() {
    this.observer.disconnect();
    document.removeEventListener("keydown", this.onKey);
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  jump(event) {
    event.preventDefault();
    const hash = event.currentTarget.getAttribute("href");
    this.element.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", hash);
    this.setActive(hash.slice(1));
  }

  setActive(id) {
    this.linkTargets.forEach((link) => {
      link.classList.toggle("active", link.getAttribute("href") === `#${id}`);
    });
  }

  fieldChanged(event) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) return;
    this.markDirty();
    // A collapsed agent card's summary quotes the model and provider; keep
    // it in sync while typing so the summary never shows a stale value.
    if (el.id.endsWith("_model")) {
      const span = this.element.querySelector(`[data-model-summary="${el.id}"]`);
      if (span) span.textContent = el.value.trim() || "no model set";
    } else if (el.id.endsWith("_provider")) {
      const span = this.element.querySelector(`[data-provider-summary="${el.id}"]`);
      if (span) span.textContent = el.value;
    }
  }

  markDirty() {
    if (this.dirty) return;
    this.dirty = true;
    this.savebarTarget?.classList.add("is-dirty");
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = "Unsaved changes";
  }

  // The POST navigates; the leave guard must not fire for it.
  submitting() {
    this.dirty = false;
  }

  reveal(event) {
    const btn = event.currentTarget;
    const input = btn.closest(".secret-row").querySelector("input");
    const show = input.type === "password";
    input.type = show ? "text" : "password";
    btn.classList.toggle("is-on", show);
    btn.setAttribute("aria-label", show ? "Hide" : "Show");
  }

  // The chip pairs with a hidden input the parser reads like the old
  // checkbox: value "on" means clear the stored secret.
  toggleClear(event) {
    const btn = event.currentTarget;
    const hidden = btn.closest(".secret-actions").querySelector("[data-clear]");
    const clearing = btn.getAttribute("aria-pressed") !== "true";
    btn.setAttribute("aria-pressed", String(clearing));
    hidden.value = clearing ? "on" : "";
    this.markDirty();
  }
}
